/**
 * ApPortsSection — a wall-plate AP's ethernet ports, on the device sheet.
 *
 * WHY THIS EXISTS
 * The owner's words: "for the AP104 with 4 ports, I need to be able to apply a port profile and
 * also override on a port by port basis". Port profiles already existed; the override did not.
 * Deviating ONE port on ONE AP meant editing the shared profile, which silently moved every
 * other AP using it. This panel is where that exception gets expressed — and, just as
 * importantly, where it stays visible afterwards.
 *
 * THE ONE GENUINELY NEW VISUAL PROBLEM: "inherited" vs "overridden"
 * Both are normal, healthy states, so the healthy/warn/critical/info ramp is off-limits (it
 * means operational state and nothing else), and brand violet is reserved for interactive
 * affordances. The distinction is therefore STRUCTURAL, defined once as .md-port in
 * styles/global.css:
 *
 *     inherited   dashed hairline, recessed ground, muted ink    — borrowed, low weight
 *     overridden  solid hairline + a 3px INK SPINE, raised       — owned here, high weight
 *     uplink      diagonal hatch                                 — locked, not yours to change
 *
 * Border style, ground and weight survive greyscale, colour blindness and both themes (every
 * value is a token that swaps with the theme), and each chip still SAYS which layer set it
 * ("from Rooms" / "on this AP") — the glance and the reading agree.
 *
 * NOTHING APPLIES ON A FIRST CLICK
 * These are live APs carrying a lab, and a uCentral push replaces the AP's ENTIRE
 * configuration. Editing a port stages a change; "Preview" calls /ap-ports/plan (read-only,
 * returns a token); the dialog shows the exact per-port before/after FOR THIS AP plus the
 * backend's warnings; only then does Apply push, with that token. A config that moved between
 * preview and confirm invalidates the token and the apply is refused.
 *
 * REVERT IS A DELETE, NOT A COPY
 * "Revert to profile" removes the override row so the port INHERITS again — a later profile
 * edit then reaches it. Writing the profile's current value into an override would look
 * identical today and quietly stop following the profile tomorrow.
 *
 * LIVE VIEW FIRST (owner, 2026-10-12, JH-BasementTV): the faceplate tiles said "LAN 1–4 Untagged
 * device config" and nothing about who was plugged in or what the port carried. The section now
 * leads with LivePortTable (link, mode & VLAN chips, clients / LLDP, traffic, with the state
 * report's age); the layer tiles survive only as the fallback for a read_only caller, who may not
 * read the live view. Each port's layer ("from profile" / "overridden on this AP") and the
 * "Edit port" entry into the override editor sit in the table's expanded row.
 *
 * THE UPLINK IS NEVER OFFERED. It is the path the controller reaches the AP on; the backend
 * refuses it and the chip is hatched and inert so the rule is visible, not just enforced.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useApPorts,
  usePlanApPorts,
  useApplyApPorts,
  useWiredNetworks,
  usePortProfiles,
  useAssignPortProfile,
  useClearPortProfile,
} from "@/lib/api/hooks";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { RoleGate } from "@/components/auth/RoleGate";
import { cn } from "@/lib/cn";
import { LivePortTable } from "@/components/site/LivePortTable";
import type { ApPortChange, ApPortRow, ApPortsPlan, WiredNetwork } from "@/types/domain";

// min-w-0 + max-w-full (UI review 2026-10-17): a <select> sizes to its longest option (a long
// profile or wired-network name) and, as a flex item, will not shrink below it -- in the 448px
// device sheet it pushed past the right edge. These let it shrink and wrap with its row.
const INPUT =
  "min-w-0 max-w-full rounded border border-line bg-surface px-2 py-1 text-sm text-ink outline-none focus:border-brand";

/** "LAN2" -> "LAN 2"; a glob like "LAN*" is left alone (it IS the port, see port_source). */
function portLabel(port: string) {
  const m = /^([A-Za-z]+)(\d+)$/.exec(port);
  return m ? `${m[1]} ${m[2]}` : port;
}

/** What a port's value says on one line: the network name if it has one, else the VLAN. */
function valueLabel(row: ApPortRow): string {
  const v = row.effective;
  if (row.uplink) return "Trunk";
  if (v.network_missing) return "Network deleted";
  if (v.network_name) return v.network_name;
  if (v.vlan != null) return `VLAN ${v.vlan}`;
  return "Untagged";
}

/** Which layer set this port, in words (shown beside "Edit port" in the live table). */
function layerText(row: ApPortRow): string {
  if (row.uplink) return "Uplink — not configurable here";
  if (!row.manageable) return row.reason ?? "Not configurable";
  if (row.overridden) return `Overridden on this AP: ${valueLabel(row)}`;
  return row.source === "profile" ? `From profile: ${valueLabel(row)}` : `Device config: ${valueLabel(row)}`;
}

export function ApPortsSection({
  serial,
  siteId,
}: {
  serial: string;
  /** The AP's site — wired networks (the named VLANs a port can bridge to) are per-site. */
  siteId: string | null;
}) {
  const { data, isLoading, error } = useApPorts(serial);
  const networks = useWiredNetworks(siteId);
  const [selected, setSelected] = useState<string | null>(null);
  const [staged, setStaged] = useState<ApPortChange[]>([]);
  const plan = usePlanApPorts(serial);
  const apply = useApplyApPorts(serial);

  const rows = data?.ports ?? [];
  const row = useMemo(() => rows.find((r) => r.port === selected) ?? null, [rows, selected]);
  const overrideCount = rows.filter((r) => r.overridden).length;

  // The LIVE table (link, VLANs, clients, LLDP, traffic) renders whatever the override layer
  // can or cannot say: an AP with no editable port (OAP101 / SonicFi are WAN-only) still has a
  // live uplink worth seeing.
  const byName = new Map(rows.map((r) => [r.port, r] as const));
  const live = (
    <LivePortTable
      serial={serial}
      renderPortActions={(p) => {
        const r = byName.get(p.name);
        if (!r) return null;
        return (
          <span className="flex items-center gap-2">
            <span className="text-[11px] text-ink-subtle">{layerText(r)}</span>
            {r.manageable && (
              <RoleGate min="site_operator" mode="hide">
                <Button variant="ghost" onClick={() => setSelected(selected === r.port ? null : r.port)}>
                  {selected === r.port ? "Close editor" : "Edit port"}
                </Button>
              </RoleGate>
            )}
          </span>
        );
      }}
      // read_only cannot read the live view (site_operator+): keep the layer faceplate for them.
      fallback={
        rows.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap gap-1.5">
              {rows.map((r) => (
                <PortChip
                  key={r.port}
                  row={r}
                  selected={selected === r.port}
                  onSelect={() => setSelected(selected === r.port ? null : r.port)}
                />
              ))}
            </div>
            <Legend />
          </div>
        ) : null
      }
    />
  );

  if (error) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorBanner error={error} />
        {live}
      </div>
    );
  }
  if (isLoading || !data) return live;
  // owgw holds no config for this AP, or the AP exposes no ethernet ports (OAP101 is WAN-only):
  // nothing to override, but the live view still applies.
  if (!data.owgw_available || rows.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {live}
        {data.reason && <p className="text-xs text-ink-subtle">{data.reason}</p>}
      </div>
    );
  }

  /** Stage one change (replacing any already staged for that port) and open the preview. */
  const stage = (change: ApPortChange) => {
    const next = [...staged.filter((c) => c.port !== change.port), change];
    setStaged(next);
    plan.reset();
    apply.reset();
    plan.mutate({ changes: next });
  };

  const closePreview = () => {
    plan.reset();
    apply.reset();
    setStaged([]);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Which profile is the base layer, and how many ports have stepped off it. Meraki puts
          the same sentence on its device summary; it is the fastest way to answer "why is this
          port on that VLAN?" without opening anything. */}
      <p className="text-xs text-ink-muted">
        Port profile:{" "}
        {data.profile ? (
          <span className="font-medium text-ink">{data.profile.name}</span>
        ) : (
          <span className="text-ink-subtle">none — ports are per-device only</span>
        )}
        {overrideCount > 0 && (
          <>
            {" · "}
            <span className="font-medium text-ink">
              {overrideCount} port{overrideCount === 1 ? "" : "s"} overridden on this AP
            </span>
          </>
        )}
      </p>

      {/* Assign a profile FROM THE DEVICE SHEET (not only the Assign APs tab). Apply renders the
          profile onto this AP and pushes it (owgw + audit); Clear detaches it without reverting
          the live config. Both are accountable writes, so they are gated to site_operator+. */}
      <RoleGate min="site_operator" mode="hide">
        <ProfileAssign serial={serial} siteId={siteId} currentProfileId={data.profile?.id ?? null} />
      </RoleGate>

      {/* The LIVE port table (owner 2026-10-12: the static tiles hid the clients and VLANs).
          Ports in physical order, uplink first; a row opens the clients / LLDP / counters, and
          "Edit port" there opens the override editor below. */}
      {live}

      {/* Where the port COUNT came from. An operator should never have to guess whether the
          four ports on screen are the AP's real ports or a number we assumed from its model. */}
      <p className="text-[11px] text-ink-subtle">
        {data.port_source === "capabilities"
          ? "Ports reported by the AP's own capabilities."
          : "The AP has not reported its capabilities, so its ports are read from the port groups its configuration uses — per-port splitting is unavailable until it does."}
      </p>

      {row && (
        <PortEditor
          row={row}
          networks={networks.data?.items ?? []}
          onStage={stage}
          busy={plan.isPending}
        />
      )}

      {overrideCount > 0 && (
        <RoleGate min="site_operator" mode="hide">
          <button
            type="button"
            className="self-start text-xs font-medium text-brand hover:underline"
            onClick={() => {
              const changes: ApPortChange[] = rows
                .filter((r) => r.overridden)
                .map((r) => ({ port: r.port, action: "revert" }));
              setStaged(changes);
              plan.reset();
              apply.reset();
              plan.mutate({ changes });
            }}
          >
            Revert all ports to the profile
          </button>
        </RoleGate>
      )}

      {plan.isError && (
        <span className="text-xs text-critical">{(plan.error as Error)?.message}</span>
      )}

      {/* PROPOSE-THEN-CONFIRM. Opens only once the plan is back, so the dialog always has the
          real before/after to show rather than a spinner pretending to be a review. */}
      {plan.data && (
        <PreviewDialog
          plan={plan.data}
          staged={staged}
          applying={apply.isPending}
          error={apply.isError ? (apply.error as Error)?.message : null}
          applied={apply.isSuccess}
          onApply={() =>
            apply.mutate(
              { changes: staged, plan_token: plan.data!.plan_token },
              { onSuccess: () => setSelected(null) },
            )
          }
          onClose={closePreview}
        />
      )}
    </div>
  );
}

/**
 * Assign a port profile to THIS AP from the device sheet.
 *
 * The Assign APs tab (routes/ApPortProfiles) can bulk-assign, but the owner also needs to do it
 * from the one AP he is looking at. A selector of the site's profiles + Apply (POST
 * /devices/<serial>/port-profile) + Clear (DELETE). Apply is the accountable device write: the
 * backend renders the profile onto the live config, pushes via owgw and writes an audit row.
 *
 * The per-port override tiles above are the exception layer on top of whatever profile is the
 * base; assigning here changes the base, and any override an operator pinned to a port stays
 * (the backend resolves both layers). "Revert to profile" still lives on each port tile.
 */
function ProfileAssign({
  serial,
  siteId,
  currentProfileId,
}: {
  serial: string;
  siteId: string | null;
  currentProfileId: number | null;
}) {
  const profiles = usePortProfiles(siteId);
  const assign = useAssignPortProfile();
  const clear = useClearPortProfile();
  // The selector starts on the AP's current profile so it reads as "what is applied", and Apply
  // is only enabled once a profile is chosen.
  const [choice, setChoice] = useState<string>(currentProfileId != null ? String(currentProfileId) : "");
  const items = profiles.data?.items ?? [];
  const busy = assign.isPending || clear.isPending;
  const warnings = assign.data?.warnings ?? [];

  // No site (unassigned device) -> profiles are per-site, so there is nothing to offer.
  if (!siteId) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <select
          className={`${INPUT} flex-1 basis-40`}
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value);
            assign.reset();
            clear.reset();
          }}
          aria-label="Port profile"
        >
          <option value="">Choose a profile…</option>
          {items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.is_default ? " (default)" : ""}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          disabled={!choice || busy || Number(choice) === currentProfileId}
          onClick={() => {
            assign.reset();
            clear.reset();
            assign.mutate({ serial, profile_id: Number(choice) });
          }}
        >
          {assign.isPending ? "Applying…" : "Apply profile"}
        </Button>
        {currentProfileId != null && (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              assign.reset();
              clear.reset();
              clear.mutate(serial, { onSuccess: () => setChoice("") });
            }}
          >
            {clear.isPending ? "Clearing…" : "Clear"}
          </Button>
        )}
        {assign.isSuccess && !assign.isPending && (
          <span className="text-xs text-healthy">
            {assign.data?.changed ? "Applied." : "Applied (no config change)."}
          </span>
        )}
        {clear.isSuccess && !clear.isPending && <span className="text-xs text-healthy">Cleared.</span>}
      </div>

      {items.length === 0 && !profiles.isLoading && (
        <span className="text-[11px] text-ink-subtle">
          No port profiles at this site yet. Create one under Wireless &gt; AP Port Profiles.
        </span>
      )}

      {/* The soft-skip notes + allow-list cautions the backend returned: which ports applied and
          which were skipped because this AP does not have them. */}
      {warnings.length > 0 && (
        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-[11px] text-ink-muted">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {assign.isError && (
        <span className="text-xs text-critical">{(assign.error as Error)?.message || "Apply failed."}</span>
      )}
      {clear.isError && (
        <span className="text-xs text-critical">{(clear.error as Error)?.message || "Clear failed."}</span>
      )}
    </div>
  );
}

/**
 * One port on the faceplate. `data-layer` (not a class soup) picks the treatment, so the DATA
 * selects it: an inherited port can never be painted as an override.
 */
function PortChip({
  row,
  selected,
  onSelect,
}: {
  row: ApPortRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const layer = row.uplink || !row.manageable ? "locked" : row.overridden ? "overridden" : "inherited";
  const provenance = row.uplink
    ? "uplink"
    : !row.manageable
      ? "unmanaged"
      : row.overridden
        ? "on this AP"
        : row.source === "profile"
          ? "from profile"
          : "device config";

  return (
    <button
      type="button"
      className="md-port"
      data-layer={layer}
      data-selected={selected}
      aria-pressed={selected}
      disabled={!row.manageable}
      onClick={onSelect}
      title={
        row.manageable
          ? `${row.port} — ${valueLabel(row)} (${row.overridden ? "overridden on this AP" : row.source === "profile" ? "inherited from the port profile" : "from the AP's own configuration"})`
          : `${row.port} — ${row.reason ?? "not configurable"}`
      }
    >
      <span className="font-mono text-[11px] font-medium leading-none">{portLabel(row.port)}</span>
      <span className="max-w-[6rem] truncate text-[11px] leading-tight">{valueLabel(row)}</span>
      {/* The third line is the provenance in words. The structural encoding is for the glance;
          this is what makes it unambiguous on the second look — and it is the channel that
          survives a screenshot pasted into a ticket. */}
      <span className="text-[10px] leading-none text-ink-subtle">{provenance}</span>
    </button>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-subtle">
      <span className="inline-flex items-center gap-1">
        <span className="md-port inline-block h-3 w-5 min-w-0 p-0" data-layer="inherited" />
        inherited from the profile
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="md-port inline-block h-3 w-5 min-w-0 p-0" data-layer="overridden" />
        overridden on this AP
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="md-port inline-block h-3 w-5 min-w-0 p-0" data-layer="locked" />
        uplink — not configurable
      </span>
    </div>
  );
}

/**
 * The selected port's editor. Shows BOTH layers side by side — what the profile says and what
 * this AP would do — because the decision an operator is making is exactly "should this port
 * keep following the profile?", and that question is unanswerable if the profile's value is
 * hidden behind the override.
 */
function PortEditor({
  row,
  networks,
  onStage,
  busy,
}: {
  row: ApPortRow;
  networks: WiredNetwork[];
  onStage: (change: ApPortChange) => void;
  busy: boolean;
}) {
  // "" = untagged; "net:<id>" = a named wired network; "vlan:<n>" is typed in the VLAN box.
  const initial = row.effective.network_id != null ? `net:${row.effective.network_id}` : "";
  const [choice, setChoice] = useState(initial);
  const [vlan, setVlan] = useState(
    row.effective.network_id == null && row.effective.vlan != null ? String(row.effective.vlan) : "",
  );

  const change: ApPortChange = choice.startsWith("net:")
    ? { port: row.port, action: "override", role: "access", network_id: Number(choice.slice(4)) }
    : vlan.trim()
      ? { port: row.port, action: "override", role: "access", vlan: Number(vlan) }
      : { port: row.port, action: "override", role: "access" };

  return (
    <div className="md-card flex min-w-0 flex-col gap-3 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex items-baseline gap-1.5">
          <span className="font-mono text-sm font-medium text-ink">{portLabel(row.port)}</span>
          {/* The friendly wall-plate name from the profile ("TV"), when the profile named it. */}
          {row.effective.label && (
            <span className="text-sm font-medium text-ink">{row.effective.label}</span>
          )}
        </span>
        <span className="text-[11px] text-ink-subtle">
          {row.overridden
            ? row.matches_profile
              ? "Overridden — currently the same value as the profile"
              : "Overridden on this AP"
            : row.source === "profile"
              ? "Inheriting from the port profile"
              : "Set in this AP's own configuration"}
        </span>
      </div>

      {/* The two layers, stated. */}
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-ink-subtle">Profile says</dt>
          <dd className="mt-0.5 text-ink-muted">
            {row.profile
              ? row.profile.network_name ??
                (row.profile.vlan != null ? `VLAN ${row.profile.vlan}` : "Untagged")
              : "— (the profile does not map this port)"}
          </dd>
        </div>
        <div>
          <dt className="text-ink-subtle">This AP</dt>
          <dd className="mt-0.5 font-medium text-ink">{valueLabel(row)}</dd>
        </div>
      </dl>

      <RoleGate
        min="site_operator"
        mode="hide"
        fallback={<p className="text-xs text-ink-muted">You have read-only access.</p>}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <select
            className={`${INPUT} flex-1 basis-40`}
            value={choice}
            onChange={(e) => {
              setChoice(e.target.value);
              if (e.target.value.startsWith("net:")) setVlan("");
            }}
            aria-label="Wired network"
          >
            <option value="">Untagged / VLAN…</option>
            {networks.map((n) => (
              <option key={n.id} value={`net:${n.id}`}>
                {n.name} (VLAN {n.vlan_id})
              </option>
            ))}
          </select>
          {!choice && (
            <input
              className={`${INPUT} w-28`}
              inputMode="numeric"
              placeholder="VLAN (opt)"
              value={vlan}
              onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, ""))}
              aria-label="VLAN id"
            />
          )}
          <Button variant="primary" disabled={busy} onClick={() => onStage(change)}>
            {busy ? "Previewing…" : "Preview change"}
          </Button>
          {row.overridden && (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => onStage({ port: row.port, action: "revert" })}
            >
              Revert to profile
            </Button>
          )}
        </div>
        <p className="text-[11px] text-ink-subtle">
          {row.overridden
            ? "Reverting deletes the override, so this port follows the profile again — including future edits to it."
            : "Overriding stops this port following the profile. Later profile edits will not reach it until you revert."}
        </p>
      </RoleGate>
    </div>
  );
}

/**
 * The CONFIRM half. Per-port before/after for THIS AP, the backend's warnings verbatim, then
 * Apply. A count ("3 ports change") is not reviewable; three rows naming the ports are.
 */
function PreviewDialog({
  plan,
  staged,
  applying,
  applied,
  error,
  onApply,
  onClose,
}: {
  plan: ApPortsPlan;
  staged: ApPortChange[];
  applying: boolean;
  applied: boolean;
  error: string | null;
  onApply: () => void;
  onClose: () => void;
}) {
  const before = new Map(plan.ports_before.map((r) => [r.port, r]));
  const touched = new Set(staged.map((c) => c.port));
  const rows = plan.ports_after.filter(
    (r) => touched.has(r.port) || before.get(r.port)?.effective.vlan !== r.effective.vlan,
  );

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none md:max-h-[85vh]">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {applied ? "Applied" : "Review this change"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            {plan.changed
              ? "This is exactly what will be pushed to this AP. Nothing has been sent yet."
              : "This AP is already in the target state — applying would push nothing."}
          </Dialog.Description>

          <table className="mt-4 w-full text-left text-xs">
            <thead className="text-ink-subtle">
              <tr>
                <th className="pb-1 font-medium">Port</th>
                <th className="pb-1 font-medium">Now</th>
                <th className="pb-1 font-medium">After</th>
                <th className="pb-1 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const b = before.get(r.port);
                return (
                  <tr key={r.port} className="border-t border-line">
                    <td className="py-1.5 font-mono">{portLabel(r.port)}</td>
                    <td className="py-1.5 text-ink-muted">{b ? valueLabel(b) : "—"}</td>
                    <td
                      className={cn(
                        "py-1.5",
                        b && b.effective.vlan !== r.effective.vlan
                          ? "font-medium text-ink"
                          : "text-ink-muted",
                      )}
                    >
                      {valueLabel(r)}
                    </td>
                    <td className="py-1.5 text-ink-subtle">
                      {r.overridden ? "override" : r.source === "profile" ? "profile" : "device"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {plan.warnings.length > 0 && (
            <ul className="mt-3 flex list-disc flex-col gap-1 pl-4 text-xs text-ink-muted">
              {plan.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          {error && <p className="mt-3 text-xs text-critical">{error}</p>}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              {applied ? "Close" : "Cancel"}
            </Button>
            {!applied && (
              <Button variant="primary" disabled={applying} onClick={onApply}>
                {applying ? "Applying…" : "Apply to this AP"}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
